import type { PersistStorage, StorageValue } from 'zustand/middleware';
import {
    PERSIST_STORE_VERSION,
    migrateDesignSchemesBucket,
    migrateGlobalBucket,
    migrateLayersBucket,
    getCurrentBucketVersions,
    getDefaultDesignSchemesBucketState,
    getDefaultGlobalBucketState,
    getDefaultLayersBucketState,
    type PersistedBucketVersions,
    type PersistedStoreSlice,
    type PersistedBuckets,
} from './persistedBuckets';
import {
    validateDesignSchemesBucketState,
    validateGlobalBucketState,
    validateLayersBucketState,
    validatePersistedBuckets,
} from './persistedSchemas';

type PersistedStoragePayload = StorageValue<unknown> & {
    bucketVersions?: PersistedBucketVersions;
};

const DEFAULT_BUCKET_VERSION_FALLBACK = 0;

const parseJson = <T>(value: string | null): T | null => {
    if (!value) return null;
    try {
        return JSON.parse(value) as T;
    } catch {
        return null;
    }
};

const stringifyJson = (value: unknown): string => JSON.stringify(value);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const isValidVersion = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;

const readBucketVersion = (value: unknown, key: keyof PersistedBucketVersions, fallback: number): number => {
    if (!isRecord(value)) return fallback;
    const maybeVersion = value[key];
    return isValidVersion(maybeVersion) ? maybeVersion : fallback;
};

const readValidatedGlobalBucket = (value: unknown): PersistedBuckets['global'] => {
    const parsed = validateGlobalBucketState(value);
    if (parsed.success) return parsed.data;
    console.warn(`[persist] Invalid global bucket, fallback to defaults. ${parsed.error}`);
    return getDefaultGlobalBucketState();
};

const readValidatedLayersBucket = (value: unknown): PersistedBuckets['layers'] => {
    const parsed = validateLayersBucketState(value);
    if (parsed.success) return parsed.data;
    console.warn(`[persist] Invalid layers bucket, fallback to defaults. ${parsed.error}`);
    return getDefaultLayersBucketState();
};

const readValidatedDesignSchemesBucket = (value: unknown): PersistedBuckets['designSchemes'] => {
    const parsed = validateDesignSchemesBucketState(value);
    if (parsed.success) return parsed.data;
    console.warn(`[persist] Invalid design-schemes bucket, fallback to defaults. ${parsed.error}`);
    return getDefaultDesignSchemesBucketState();
};

const getDefaultBuckets = (): PersistedBuckets => ({
    global: getDefaultGlobalBucketState(),
    layers: getDefaultLayersBucketState(),
    designSchemes: getDefaultDesignSchemesBucketState(),
});

const migrateAndValidateBuckets = (
    globalState: unknown,
    globalVersion: number,
    layersState: unknown,
    layersVersion: number,
    designSchemesState: unknown,
    designSchemesVersion: number
): PersistedBuckets => {
    const buckets: PersistedBuckets = {
        global: readValidatedGlobalBucket(migrateGlobalBucket(globalState, globalVersion)),
        layers: readValidatedLayersBucket(migrateLayersBucket(layersState, layersVersion)),
        designSchemes: readValidatedDesignSchemesBucket(migrateDesignSchemesBucket(designSchemesState, designSchemesVersion)),
    };

    const persistedValidation = validatePersistedBuckets(buckets);
    if (!persistedValidation.success) {
        console.warn(`[persist] Invalid persisted state, fallback to defaults. ${persistedValidation.error}`);
        return getDefaultBuckets();
    }

    return persistedValidation.data;
};

export const createBucketPersistenceAdapter = (getStorage: () => Storage): PersistStorage<PersistedStoreSlice> => ({
    getItem: (name: string): StorageValue<PersistedStoreSlice> | null => {
        const storage = getStorage();

        const singlePayload = parseJson<PersistedStoragePayload>(storage.getItem(name));
        if (!singlePayload) {
            return null;
        }

        const singleState = isRecord(singlePayload) && 'state' in singlePayload ? singlePayload.state : singlePayload;
        const source = isRecord(singleState) ? singleState : {};
        const bucketVersionsSource = singlePayload.bucketVersions;

        const versions: PersistedBucketVersions = {
            global: readBucketVersion(bucketVersionsSource, 'global', DEFAULT_BUCKET_VERSION_FALLBACK),
            layers: readBucketVersion(bucketVersionsSource, 'layers', DEFAULT_BUCKET_VERSION_FALLBACK),
            designSchemes: readBucketVersion(bucketVersionsSource, 'designSchemes', DEFAULT_BUCKET_VERSION_FALLBACK),
        };

        const buckets = migrateAndValidateBuckets(
            source.global,
            versions.global,
            source.layers,
            versions.layers,
            source.designSchemes,
            versions.designSchemes
        );

        return {
            state: buckets,
            version: PERSIST_STORE_VERSION,
        };
    },

    setItem: (name: string, value: StorageValue<PersistedStoreSlice>): void => {
        const storage = getStorage();
        const buckets = value.state;
        if (!buckets) return;

        storage.setItem(
            name,
            stringifyJson({
                version: PERSIST_STORE_VERSION,
                bucketVersions: getCurrentBucketVersions(),
                state: buckets,
            })
        );
    },

    removeItem: (name: string): void => {
        const storage = getStorage();
        storage.removeItem(name);
    },
});
